import { format } from 'date-fns';
import {
  getDisplayHour12,
  getHoursFormat,
  getIntlTimeFormatOptions,
} from '@/config/hoursFormat';
import {
  convertTimeFromUTC,
  getTimezoneAbbreviation,
  getUserTimezone,
} from '@/utils/timezoneUtils';

/** date-fns token for time portion of display strings. */
export function getDateFnsTimeToken(): 'HH:mm' | 'h:mm a' {
  return getHoursFormat() === '12' ? 'h:mm a' : 'HH:mm';
}

/** User-facing labels on react-big-calendar (date-fns localizer). */
export function buildReactBigCalendarFormats(
  formatFn: (date: Date, token: string) => string,
) {
  const tf = getDateFnsTimeToken();
  const range = ({ start, end }: { start: Date; end: Date }) =>
    `${formatFn(start, tf)} – ${formatFn(end, tf)}`;
  return {
    timeGutterFormat: tf,
    selectRangeFormat: tf,
    eventTimeRangeFormat: range,
    agendaTimeFormat: tf,
    agendaTimeRangeFormat: range,
  };
}

/** e.g. "MMM d, yyyy HH:mm" or "MMM d, yyyy h:mm a" */
export function getDateFnsDateTimePattern(datePart = 'MMM d, yyyy'): string {
  return `${datePart} ${getDateFnsTimeToken()}`;
}

export function formatDateWithDisplayTime(
  date: Date,
  datePart: string,
): string {
  return format(date, `${datePart} ${getDateFnsTimeToken()}`);
}

export function formatDisplayTimeWithSeconds(
  date: Date | string,
  timeZone?: string,
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return '';
  const tz = timeZone?.trim() || getUserTimezone();
  return new Intl.DateTimeFormat(
    'en-US',
    getIntlTimeFormatOptions({
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }),
  ).format(d);
}

export function formatDisplayTimeOnly(
  date: Date | string,
  timeZone?: string,
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return '';
  const tz = timeZone?.trim() || getUserTimezone();
  return new Intl.DateTimeFormat(
    'en-US',
    getIntlTimeFormatOptions({
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
    }),
  ).format(d);
}

export function formatDisplayTimeRange(
  start: Date | string,
  end: Date | string,
  timeZone?: string,
  withTimezoneAbbr = false,
): string {
  const tz = timeZone?.trim() || getUserTimezone();
  const startDate = typeof start === 'string' ? new Date(start) : start;
  const startDisplay = formatDisplayTimeOnly(start, tz);
  const endDisplay = formatDisplayTimeOnly(end, tz);
  if (!withTimezoneAbbr) {
    return `${startDisplay} - ${endDisplay}`;
  }
  const abbr = getTimezoneAbbreviation(
    tz,
    Number.isNaN(startDate.getTime()) ? new Date() : startDate,
  );
  return abbr
    ? `${startDisplay} - ${endDisplay} ${abbr}`
    : `${startDisplay} - ${endDisplay}`;
}

/** ISO instants → display range (booking slot lists). */
export function formatIsoTimeRangeForDisplay(
  startIso: string,
  endIso: string,
  timeZone?: string,
  withTimezoneAbbr = false,
): string {
  return formatDisplayTimeRange(startIso, endIso, timeZone, withTimezoneAbbr);
}

/** Wall-clock HH:mm → display range (client-generated booking slot lists). */
export function formatWallClockTimeRangeForDisplay(
  startHm: string,
  endHm: string,
  timeZone?: string,
  withTimezoneAbbr = false,
): string {
  const startDisplay = formatHmForDisplay(startHm);
  const endDisplay = formatHmForDisplay(endHm);
  if (!withTimezoneAbbr) {
    return `${startDisplay} - ${endDisplay}`;
  }
  const tz = timeZone?.trim();
  if (!tz) {
    return `${startDisplay} - ${endDisplay}`;
  }
  const abbr = getTimezoneAbbreviation(tz);
  return abbr
    ? `${startDisplay} - ${endDisplay} ${abbr}`
    : `${startDisplay} - ${endDisplay}`;
}

/** Full datetime for tables/exports: "05/25/2026, 3:30 PM" per env. */
export function formatDisplayDateTimeValue(
  date: Date | string,
  options?: { withSeconds?: boolean; timeZone?: string },
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return typeof date === 'string' ? date : '';
  const tz = options?.timeZone?.trim() || getUserTimezone();
  const datePart = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
  const timePart = options?.withSeconds
    ? formatDisplayTimeWithSeconds(d, tz)
    : formatDisplayTimeOnly(d, tz);
  return `${datePart}, ${timePart}`;
}

/** e.g. "May 25, 2026, 3:30 PM" */
export function formatDisplayDateCommaTime(
  date: Date | string,
  datePartPattern = 'MMM dd, yyyy',
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return typeof date === 'string' ? date : '';
  return `${format(d, datePartPattern)}, ${formatDisplayTimeOnly(d)}`;
}

/** Date + time in one line (locale date + env time). */
export function formatDisplayDateAndTime(
  date: Date | string,
  options?: {
    dateStyle?: 'numeric' | 'medium' | 'long';
    withSeconds?: boolean;
    timeZone?: string;
  },
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return typeof date === 'string' ? date : '';
  const tz = options?.timeZone?.trim() || getUserTimezone();
  const dateOpts: Intl.DateTimeFormatOptions =
    options?.dateStyle === 'long'
      ? {
          timeZone: tz,
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        }
      : options?.dateStyle === 'medium'
        ? { timeZone: tz, month: 'short', day: 'numeric', year: 'numeric' }
        : { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' };
  const datePart = new Intl.DateTimeFormat('en-US', dateOpts).format(d);
  const timePart = options?.withSeconds
    ? formatDisplayTimeWithSeconds(d, tz)
    : formatDisplayTimeOnly(d, tz);
  return `${datePart} ${timePart}`;
}

/** Display stored wall-clock HH:mm[:ss] (API values stay 24-hour). */
export function formatWallClockTimeForDisplay(time: string): string {
  const m = String(time || '')
    .trim()
    .match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return time;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const sec = m[3] != null ? parseInt(m[3], 10) : null;
  if (Number.isNaN(h) || Number.isNaN(min)) return time;
  if (sec != null && !Number.isNaN(sec)) {
    const d = new Date(2000, 0, 1, h, min, sec);
    return formatDisplayTimeWithSeconds(d);
  }
  return formatHmForDisplay(
    `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`,
  );
}

/** UTC instant → session-type timezone label (Booked Sessions, etc.). */
export function formatScheduleTimeInTimezoneForDisplay(
  utcDate: Date,
  sessionTimezone: string | null | undefined,
): string {
  if (!sessionTimezone) {
    return formatDisplayTimeOnly(utcDate, 'UTC');
  }
  const utcHours = utcDate.getUTCHours().toString().padStart(2, '0');
  const utcMinutes = utcDate.getUTCMinutes().toString().padStart(2, '0');
  const utcTimeStr = `${utcHours}:${utcMinutes}`;
  const year = utcDate.getUTCFullYear();
  const month = (utcDate.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = utcDate.getUTCDate().toString().padStart(2, '0');
  const dateStr = `${year}-${month}-${day}`;
  const convertedTime = convertTimeFromUTC(
    utcTimeStr,
    sessionTimezone,
    dateStr,
  );
  return formatHmForDisplay(convertedTime);
}

/**
 * Display label for wall-clock HH:mm (values sent to API stay 24-hour).
 */
/** Label for hour-only selects (00–23); option `value` stays 24h padded. */
export function formatHourOnlyForDisplay(hour24: string | number): string {
  const h =
    typeof hour24 === 'number'
      ? hour24
      : parseInt(String(hour24).trim(), 10);
  if (Number.isNaN(h) || h < 0 || h > 23) {
    return String(hour24);
  }
  if (getHoursFormat() === '24') {
    return String(h).padStart(2, '0');
  }
  const d = new Date(2000, 0, 1, h, 0, 0);
  return new Intl.DateTimeFormat(
    'en-US',
    getIntlTimeFormatOptions({ hour: 'numeric' }),
  ).format(d);
}

/** Width class for hour/minute selects when labels are wider in 12h mode. */
export function getTimeSelectTriggerClass(base = 'h-9 bg-white'): string {
  const width = getHoursFormat() === '12' ? 'w-[5.25rem]' : 'w-16';
  return `${width} ${base}`;
}

export function formatHmForDisplay(hm: string): string {
  const m = String(hm || '').trim().match(/^(\d{1,2}):(\d{2})/);
  if (!m) return hm;
  const hours = parseInt(m[1], 10);
  const minutes = parseInt(m[2], 10);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return hm;
  if (getHoursFormat() === '24') {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }
  const d = new Date();
  d.setHours(hours, minutes, 0, 0);
  return new Intl.DateTimeFormat(
    'en-US',
    getIntlTimeFormatOptions({ hour: 'numeric', minute: '2-digit' }),
  ).format(d);
}

/** Calendar Y-axis hour in session timezone (hour is 0–23 wall clock for that column day). */
export function formatCalendarHourLabel(
  hour: number,
  timeZone: string,
  anchorYmd: string,
  formatZonedWallClock?: (ymd: string, h: number, m: number, tz: string) => number | null,
): string {
  if (getHoursFormat() === '24') {
    return `${String(hour).padStart(2, '0')}:00`;
  }
  if (formatZonedWallClock) {
    const ms = formatZonedWallClock(anchorYmd, hour, 0, timeZone);
    if (ms != null) {
      return new Intl.DateTimeFormat(
        'en-US',
        getIntlTimeFormatOptions({
          timeZone,
          hour: 'numeric',
          minute: '2-digit',
        }),
      ).format(new Date(ms));
    }
  }
  return format(new Date(0, 0, 0, hour, 0, 0), 'h a');
}

const pad2 = (n: number) => String(Math.trunc(n)).padStart(2, '0');

/** Normalize to HH:mm or HH:mm:ss (24-hour storage). */
export function normalizeWallClockHm(
  value: string,
  withSeconds = false,
): string {
  const m = String(value || '')
    .trim()
    .match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return withSeconds ? '00:00:00' : '00:00';
  const h = Math.min(23, Math.max(0, parseInt(m[1], 10)));
  const min = Math.min(59, Math.max(0, parseInt(m[2], 10)));
  const sec =
    m[3] != null ? Math.min(59, Math.max(0, parseInt(m[3], 10))) : 0;
  if (withSeconds) return `${pad2(h)}:${pad2(min)}:${pad2(sec)}`;
  return `${pad2(h)}:${pad2(min)}`;
}

export function dateToWallClockHm(date: Date, withSeconds = false): string {
  if (Number.isNaN(date.getTime())) {
    return withSeconds ? '00:00:00' : '00:00';
  }
  return normalizeWallClockHm(
    `${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}`,
    withSeconds,
  );
}

export function applyWallClockHmToDate(base: Date, hm: string): Date {
  const withSeconds = hm.trim().split(':').length > 2;
  const norm = normalizeWallClockHm(hm, withSeconds);
  const [h, min, sec = '0'] = norm.split(':').map((x) => parseInt(x, 10));
  const d = new Date(base);
  d.setHours(h, min, sec, 0);
  return d;
}

export type TwelveHourParts = {
  hour12: string;
  minute: string;
  period: 'AM' | 'PM';
};

export function hm24ToTwelveHourParts(hm: string): TwelveHourParts {
  const norm = normalizeWallClockHm(hm);
  let h24 = parseInt(norm.split(':')[0], 10);
  const minute = norm.split(':')[1];
  const period: 'AM' | 'PM' = h24 >= 12 ? 'PM' : 'AM';
  let hour12 = h24 % 12;
  if (hour12 === 0) hour12 = 12;
  return { hour12: String(hour12), minute, period };
}

export function twelveHourPartsToHm24(parts: TwelveHourParts): string {
  let h = parseInt(parts.hour12, 10);
  if (Number.isNaN(h) || h < 1 || h > 12) h = 12;
  const minute = pad2(parseInt(parts.minute, 10) || 0);
  if (parts.period === 'AM') {
    if (h === 12) h = 0;
  } else if (h !== 12) {
    h += 12;
  }
  return normalizeWallClockHm(`${h}:${minute}`);
}

export const WALL_CLOCK_HOUR_OPTIONS_24 = Array.from({ length: 24 }, (_, i) =>
  i.toString().padStart(2, '0'),
);

export const WALL_CLOCK_MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) =>
  i.toString().padStart(2, '0'),
);

export const TWELVE_HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) =>
  String(i + 1),
);

export { getDisplayHour12, getHoursFormat, getIntlTimeFormatOptions };
